import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { X, Sparkles, Check, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "../components/ui/sonner";

const currencySymbols = { AED: "AED", GBP: "\u00a3", EUR: "\u20ac", TRY: "\u20ba" };

export default function AssetVerificationModal({ open, onClose, onSuccess, extractedData, documentName }) {
  const [form, setForm] = useState({
    name: "", location: "dubai", currency: "AED", purchase_price: "",
    purchase_date: "", property_type: "residential", description: "",
    area_sqft: "", monthly_rental_income: "", service_charges_annual: "",
    service_fee_per_sqft: "", maintenance_per_sqft: "", other_annual_costs: "",
    other_purchase_costs: "", broker_commission: "", dld_fee: "", trustee_fee: "", source_document_id: "",
    is_financed: false, loan_amount: "", interest_rate_pct: "", amortization_pct: "",
    // North Cyprus
    is_new_build: false,
    transformer_fee: "", solicitor_fee: "", property_tax_annual: "",
    // Germany
    bundesland: "bayern", grunderwerbsteuer: "", notary_fee: "",
    renovation_cost: "", hausgeld_monthly: "", instandhaltungsruecklage_monthly: "",
    // Deal-Analyzer-style additive opex (May 2026 harmonisation):
    insurance_annual: 0, management_fee_pct: 10,
    maintenance_reserve_pct: 1, vacancy_reserve_pct: 2,
  });

  const BUNDESLAENDER = [
    { code: "bayern", label: "Bayern (3.5%)" },
    { code: "sachsen", label: "Sachsen (3.5%)" },
    { code: "hamburg", label: "Hamburg (5.5%)" },
    { code: "baden_wuerttemberg", label: "Baden-W\u00fcrttemberg (5.0%)" },
    { code: "bremen", label: "Bremen (5.0%)" },
    { code: "niedersachsen", label: "Niedersachsen (5.0%)" },
    { code: "rheinland_pfalz", label: "Rheinland-Pfalz (5.0%)" },
    { code: "sachsen_anhalt", label: "Sachsen-Anhalt (5.0%)" },
    { code: "mecklenburg_vorpommern", label: "Mecklenburg-Vorpommern (6.0%)" },
    { code: "berlin", label: "Berlin (6.0%)" },
    { code: "hessen", label: "Hessen (6.0%)" },
    { code: "brandenburg", label: "Brandenburg (6.5%)" },
    { code: "nrw", label: "Nordrhein-Westfalen (6.5%)" },
    { code: "saarland", label: "Saarland (6.5%)" },
    { code: "schleswig_holstein", label: "Schleswig-Holstein (6.5%)" },
    { code: "thueringen", label: "Th\u00fcringen (6.5%)" },
  ];
  const [roi, setRoi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pre-fill from AI extraction
  useEffect(() => {
    if (extractedData && open) {
      setForm({
        name: extractedData.name || "",
        location: extractedData.location || "dubai",
        currency: extractedData.currency || "AED",
        purchase_price: extractedData.purchase_price || "",
        purchase_date: extractedData.purchase_date || "",
        property_type: extractedData.property_type || "residential",
        description: extractedData.description || "",
        area_sqft: extractedData.area_sqft || "",
        monthly_rental_income: extractedData.monthly_rental_income || "",
        service_charges_annual: extractedData.service_charges_annual || "",
        source_document_id: extractedData.source_document_id || ""
      });
    }
  }, [extractedData, open]);

  // Live ROI calculation (location-aware)
  const calculateRoi = useCallback(async () => {
    const price = parseFloat(form.purchase_price);
    if (!price) { setRoi(null); return; }
    try {
      const { data } = await api.post("/assets/calculate-roi", {
        purchase_price: price,
        monthly_rental_income: parseFloat(form.monthly_rental_income) || 0,
        location: form.location,
        area_sqft: parseFloat(form.area_sqft) || 0,
        service_fee_per_sqft: parseFloat(form.service_fee_per_sqft) || 0,
        maintenance_per_sqft: parseFloat(form.maintenance_per_sqft) || 0,
        service_charges_annual: parseFloat(form.service_charges_annual) || 0,
        other_annual_costs: parseFloat(form.other_annual_costs) || 0,
        other_purchase_costs: parseFloat(form.other_purchase_costs) || 0,
        broker_commission: parseFloat(form.broker_commission) || undefined,
        dld_fee: parseFloat(form.dld_fee) || undefined,
        trustee_fee: parseFloat(form.trustee_fee) || undefined,
        is_financed: form.is_financed,
        loan_amount: parseFloat(form.loan_amount) || 0,
        interest_rate_pct: parseFloat(form.interest_rate_pct) || 0,
        amortization_pct: parseFloat(form.amortization_pct) || 0,
        is_new_build: form.is_new_build,
        transformer_fee: parseFloat(form.transformer_fee) || 0,
        solicitor_fee: parseFloat(form.solicitor_fee) || 0,
        property_tax_annual: parseFloat(form.property_tax_annual) || 0,
        // Germany
        bundesland: form.bundesland,
        grunderwerbsteuer: parseFloat(form.grunderwerbsteuer) || undefined,
        notary_fee: parseFloat(form.notary_fee) || undefined,
        renovation_cost: parseFloat(form.renovation_cost) || 0,
        hausgeld_monthly: parseFloat(form.hausgeld_monthly) || 0,
        instandhaltungsruecklage_monthly: parseFloat(form.instandhaltungsruecklage_monthly) || 0,
        // Deal-Analyzer-style additive opex (May 2026 harmonisation):
        insurance_annual: parseFloat(form.insurance_annual) || 0,
        management_fee_pct: parseFloat(form.management_fee_pct) || 0,
        maintenance_reserve_pct: parseFloat(form.maintenance_reserve_pct) || 0,
        vacancy_reserve_pct: parseFloat(form.vacancy_reserve_pct) || 0,
      });
      setRoi(data);
    } catch { setRoi(null); }
  }, [form.purchase_price, form.monthly_rental_income, form.location, form.area_sqft,
      form.service_fee_per_sqft, form.maintenance_per_sqft, form.service_charges_annual,
      form.other_annual_costs, form.other_purchase_costs, form.broker_commission, form.dld_fee, form.trustee_fee,
      form.is_financed, form.loan_amount, form.interest_rate_pct, form.amortization_pct,
      form.is_new_build, form.transformer_fee,
      form.solicitor_fee, form.property_tax_annual,
      form.bundesland, form.grunderwerbsteuer, form.notary_fee,
      form.renovation_cost, form.hausgeld_monthly, form.instandhaltungsruecklage_monthly,
      form.insurance_annual, form.management_fee_pct,
      form.maintenance_reserve_pct, form.vacancy_reserve_pct]);

  useEffect(() => {
    const t = setTimeout(calculateRoi, 300);
    return () => clearTimeout(t);
  }, [calculateRoi]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.purchase_price || !form.name) {
      setError("Property name and purchase price are required");
      return;
    }

    // Financing validation: bank financing requires meaningful loan + interest figures.
    if (form.is_financed) {
      const loan = parseFloat(form.loan_amount);
      const interest = parseFloat(form.interest_rate_pct);
      if (!loan || loan <= 0) {
        toast.warning("Bank Financing is ON but Loan Amount is missing", {
          description: "Enter a positive loan amount, or switch financing OFF.",
        });
        return;
      }
      if (!interest || interest <= 0) {
        toast.warning("Bank Financing is ON but Interest Rate is missing", {
          description: "Enter a positive annual interest rate (%), otherwise EKR / Net ROI will be misleading.",
        });
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        purchase_price: parseFloat(form.purchase_price),
        area_sqft: form.area_sqft ? parseFloat(form.area_sqft) : null,
        monthly_rental_income: form.monthly_rental_income ? parseFloat(form.monthly_rental_income) : null,
        service_charges_annual: form.service_charges_annual ? parseFloat(form.service_charges_annual) : null,
        service_fee_per_sqft: form.service_fee_per_sqft ? parseFloat(form.service_fee_per_sqft) : null,
        maintenance_per_sqft: form.maintenance_per_sqft ? parseFloat(form.maintenance_per_sqft) : null,
        other_annual_costs: form.other_annual_costs ? parseFloat(form.other_annual_costs) : null,
        other_purchase_costs: form.other_purchase_costs ? parseFloat(form.other_purchase_costs) : null,
        broker_commission: form.broker_commission ? parseFloat(form.broker_commission) : null,
        dld_fee: form.dld_fee ? parseFloat(form.dld_fee) : null,
        trustee_fee: form.trustee_fee ? parseFloat(form.trustee_fee) : null,
        is_financed: form.is_financed,
        loan_amount: form.loan_amount ? parseFloat(form.loan_amount) : null,
        interest_rate_pct: form.interest_rate_pct ? parseFloat(form.interest_rate_pct) : null,
        amortization_pct: form.amortization_pct ? parseFloat(form.amortization_pct) : null,
        // North Cyprus
        is_new_build: form.is_new_build,
        transformer_fee: form.transformer_fee ? parseFloat(form.transformer_fee) : null,
        solicitor_fee: form.solicitor_fee ? parseFloat(form.solicitor_fee) : null,
        property_tax_annual: form.property_tax_annual ? parseFloat(form.property_tax_annual) : null,
        // Germany
        bundesland: form.bundesland || null,
        grunderwerbsteuer: form.grunderwerbsteuer ? parseFloat(form.grunderwerbsteuer) : null,
        notary_fee: form.notary_fee ? parseFloat(form.notary_fee) : null,
        renovation_cost: form.renovation_cost ? parseFloat(form.renovation_cost) : null,
        hausgeld_monthly: form.hausgeld_monthly ? parseFloat(form.hausgeld_monthly) : null,
        instandhaltungsruecklage_monthly: form.instandhaltungsruecklage_monthly ? parseFloat(form.instandhaltungsruecklage_monthly) : null,
        // Deal-Analyzer-style additive opex (May 2026 harmonisation):
        insurance_annual: parseFloat(form.insurance_annual) || 0,
        management_fee_pct: parseFloat(form.management_fee_pct) || 0,
        maintenance_reserve_pct: parseFloat(form.maintenance_reserve_pct) || 0,
        vacancy_reserve_pct: parseFloat(form.vacancy_reserve_pct) || 0,
      };
      await api.post("/assets", payload);
      onSuccess();
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map(e => `${Array.isArray(e.loc) ? e.loc.slice(-1)[0] : ""}: ${e.msg}`).join("; ")
        : (typeof d === "string" ? d : "Failed to save asset");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === "location") {
        updated.currency = value === "dubai" ? "AED" : value === "germany" ? "EUR" : "GBP";
      }
      return updated;
    });
  };

  const sym = currencySymbols[form.currency] || form.currency;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
          >
            <div className="bg-[#0A0A0A] border border-white/[0.12] w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="asset-verification-modal">
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-white/[0.10] sticky top-0 bg-[#0A0A0A] z-10">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                    <h2 className="font-serif text-lg text-white">Verify Extracted Data</h2>
                  </div>
                  {documentName && (
                    <p className="text-white/65 text-xs">Source: {documentName}</p>
                  )}
                </div>
                <button onClick={onClose} className="text-white/65 hover:text-white transition-colors" data-testid="close-verification-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* AI confidence badge */}
              {extractedData?.confidence != null && (
                <div className="px-5 pt-4">
                  <div className={`flex items-center gap-2 px-3 py-2 text-xs ${
                    extractedData.confidence >= 0.7 ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" :
                    extractedData.confidence >= 0.4 ? "bg-amber-500/10 border border-amber-500/20 text-amber-400" :
                    "bg-red-500/10 border border-red-500/20 text-red-400"
                  }`}>
                    {extractedData.confidence >= 0.7 ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    AI confidence: {Math.round(extractedData.confidence * 100)}% &mdash; Please verify all fields before saving
                  </div>
                </div>
              )}

              {error && (
                <div className="mx-5 mt-4 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2 text-sm">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                {/* Property Name */}
                <div>
                  <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Property Name</label>
                  <input
                    data-testid="verify-name-input"
                    value={form.name}
                    onChange={e => updateField("name", e.target.value)}
                    className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm placeholder:text-white/55 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                    required
                  />
                </div>

                {/* Location + Currency + Type */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Location</label>
                    <select data-testid="verify-location" value={form.location} onChange={e => updateField("location", e.target.value)}
                      className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none">
                      <option value="dubai">Dubai, UAE</option>
                      <option value="north_cyprus">North Cyprus</option>
                      <option value="germany">Germany</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Currency</label>
                    <select data-testid="verify-currency" value={form.currency} onChange={e => updateField("currency", e.target.value)}
                      className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none">
                      <option value="AED">AED</option><option value="GBP">GBP</option><option value="EUR">EUR</option><option value="TRY">TRY</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Type</label>
                    <select data-testid="verify-type" value={form.property_type} onChange={e => updateField("property_type", e.target.value)}
                      className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none">
                      <option value="residential">Residential</option><option value="commercial">Commercial</option><option value="off_plan">Off Plan</option>
                    </select>
                  </div>
                </div>

                {/* Price + Date + Area */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Purchase Price ({sym})</label>
                    <input data-testid="verify-price" type="number" step="0.01" value={form.purchase_price}
                      onChange={e => updateField("purchase_price", e.target.value)}
                      className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                      required />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Purchase Date</label>
                    <input data-testid="verify-date" type="date" value={form.purchase_date}
                      onChange={e => updateField("purchase_date", e.target.value)}
                      className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none transition-all" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Area ({form.location === "north_cyprus" || form.location === "germany" ? "m\u00b2" : "sq ft"})</label>
                    <input data-testid="verify-area" type="number" value={form.area_sqft}
                      onChange={e => updateField("area_sqft", e.target.value)}
                      className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] outline-none transition-all"
                      placeholder={form.location === "north_cyprus" ? "e.g. 167" : "e.g. 1450"} />
                  </div>
                </div>

                {/* Dubai Fee Breakdown (conditional) */}
                {form.location === "dubai" && roi && (
                  <div className="border border-white/[0.10] bg-white/[0.02] p-4 space-y-2">
                    <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-2">Dubai Acquisition Cost Preview</p>
                    <div className="flex justify-between text-xs"><span className="text-white/65">Purchase Price</span><span className="font-mono text-white">{sym} {parseFloat(form.purchase_price || 0).toLocaleString()}</span></div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white/65">DLD Transfer Fee (4%)</span>
                      <div className="flex items-center gap-2">
                        <input data-testid="verify-dld-fee" type="number" step="1" value={form.dld_fee}
                          onChange={e => updateField("dld_fee", e.target.value)}
                          className="w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                          placeholder={roi.dld_fee?.toLocaleString() || "auto 4%"} />
                        <span className="text-white/60 text-[9px]">{sym}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white/65">Brokerage Fees (2.1%)</span>
                      <div className="flex items-center gap-2">
                        <input data-testid="verify-broker-fee" type="number" step="1" value={form.broker_commission}
                          onChange={e => updateField("broker_commission", e.target.value)}
                          className="w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                          placeholder={roi.broker_commission?.toLocaleString() || "auto 2.1%"} />
                        <span className="text-white/60 text-[9px]">{sym}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white/65">Trustee Office Fee</span>
                      <div className="flex items-center gap-2">
                        <input data-testid="verify-trustee-fee" type="number" step="1" value={form.trustee_fee}
                          onChange={e => updateField("trustee_fee", e.target.value)}
                          className="w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                          placeholder={roi.trustee_fee?.toLocaleString() || "auto"} />
                        <span className="text-white/60 text-[9px]">{sym}</span>
                      </div>
                    </div>
                    <div className="flex justify-between text-xs pt-2 border-t border-[#D4AF37]/20"><span className="text-white font-medium">Total Investment</span><span className="font-mono text-[#D4AF37] font-medium">{sym} {roi.total_investment?.toLocaleString()}</span></div>
                  </div>
                )}

                {/* North Cyprus Acquisition Cost Preview */}
                {form.location === "north_cyprus" && (
                  <div className="border border-white/[0.10] bg-white/[0.02] p-4 space-y-2.5" data-testid="cyprus-cost-preview">
                    <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-2">North Cyprus Acquisition Cost Preview</p>
                    <div className="flex items-center justify-between pb-2 border-b border-white/[0.08]">
                      <span className="text-white/85 text-xs">New Build Project</span>
                      <button type="button" onClick={() => updateField("is_new_build", !form.is_new_build)}
                        data-testid="verify-cyprus-newbuild-toggle"
                        className={`w-9 h-5 rounded-full transition-colors relative ${form.is_new_build ? "bg-[#D4AF37]" : "bg-white/10"}`}>
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.is_new_build ? "left-[18px]" : "left-0.5"}`} />
                      </button>
                    </div>
                    <div className="flex justify-between text-xs"><span className="text-white/65">Purchase Price</span><span className="font-mono text-white">{sym} {parseFloat(form.purchase_price || 0).toLocaleString()}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-white/65">Stamp Duty (0.5%)</span><span className="font-mono text-white/85">{sym} {roi?.stamp_duty?.toLocaleString() || "0"}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-white/65">VAT (5%) {!form.is_new_build && <span className="text-white/60">&mdash; resale, n/a</span>}</span><span className="font-mono text-white/85">{sym} {roi?.vat?.toLocaleString() || "0"}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-white/65">Title Deed Transfer (12%)</span><span className="font-mono text-white/85">{sym} {roi?.transfer_fee?.toLocaleString() || "0"}</span></div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white/65">Transformer Fee {!form.is_new_build && <span className="text-white/60">&mdash; n/a</span>}</span>
                      <div className="flex items-center gap-2">
                        <input data-testid="verify-transformer-fee" type="number" step="1"
                          value={form.transformer_fee} disabled={!form.is_new_build}
                          onChange={e => updateField("transformer_fee", e.target.value)}
                          className="w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all disabled:opacity-40"
                          placeholder="e.g. 2000" />
                        <span className="text-white/60 text-[9px]">{sym}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white/65">Solicitor / Legal Fees</span>
                      <div className="flex items-center gap-2">
                        <input data-testid="verify-solicitor-fee" type="number" step="1" value={form.solicitor_fee}
                          onChange={e => updateField("solicitor_fee", e.target.value)}
                          className="w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                          placeholder="e.g. 2500" />
                        <span className="text-white/60 text-[9px]">{sym}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white/65">Other Purchase Costs</span>
                      <div className="flex items-center gap-2">
                        <input type="number" step="1" value={form.other_purchase_costs}
                          onChange={e => updateField("other_purchase_costs", e.target.value)}
                          className="w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                          placeholder="0" />
                        <span className="text-white/60 text-[9px]">{sym}</span>
                      </div>
                    </div>
                    {roi && (
                      <div className="flex justify-between text-xs pt-2 border-t border-[#D4AF37]/20"><span className="text-white font-medium">Total Investment</span><span className="font-mono text-[#D4AF37] font-medium">{sym} {roi.total_investment?.toLocaleString()}</span></div>
                    )}
                  </div>
                )}

                {/* Germany Acquisition Cost Preview */}
                {form.location === "germany" && (
                  <div className="border border-white/[0.10] bg-white/[0.02] p-4 space-y-2.5" data-testid="verify-germany-cost-preview">
                    <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-2">Germany Acquisition Cost Preview</p>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Bundesland (Grunderwerbsteuer rate)</label>
                      <select data-testid="verify-germany-bundesland" value={form.bundesland}
                        onChange={e => { updateField("bundesland", e.target.value); updateField("grunderwerbsteuer", ""); }}
                        className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] outline-none">
                        {BUNDESLAENDER.map(b => <option key={b.code} value={b.code}>{b.label}</option>)}
                      </select>
                    </div>
                    <div className="flex justify-between text-xs pt-1"><span className="text-white/65">Purchase Price</span><span className="font-mono text-white">{sym} {parseFloat(form.purchase_price || 0).toLocaleString()}</span></div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white/65">Grunderwerbsteuer ({roi?.grunderwerbsteuer_rate_pct ? `${roi.grunderwerbsteuer_rate_pct}%` : "\u2014"})</span>
                      <div className="flex items-center gap-2">
                        <input data-testid="verify-germany-grunderwerbsteuer" type="number" step="1" value={form.grunderwerbsteuer}
                          onChange={e => updateField("grunderwerbsteuer", e.target.value)}
                          className="w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                          placeholder={roi?.grunderwerbsteuer?.toLocaleString() || "auto"} />
                        <span className="text-white/60 text-[9px]">{sym}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white/65">Notar- &amp; Gerichtskosten (~1.5%)</span>
                      <div className="flex items-center gap-2">
                        <input data-testid="verify-germany-notary" type="number" step="1" value={form.notary_fee}
                          onChange={e => updateField("notary_fee", e.target.value)}
                          className="w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                          placeholder={roi?.notary_fee?.toLocaleString() || "auto 1.5%"} />
                        <span className="text-white/60 text-[9px]">{sym}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white/65">Maklerprovision (3.57% incl. VAT)</span>
                      <div className="flex items-center gap-2">
                        <input data-testid="verify-germany-broker" type="number" step="1" value={form.broker_commission}
                          onChange={e => updateField("broker_commission", e.target.value)}
                          className="w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                          placeholder={roi?.broker_commission?.toLocaleString() || "auto 3.57%"} />
                        <span className="text-white/60 text-[9px]">{sym}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white/65">Renovierung &amp; Sanierung</span>
                      <div className="flex items-center gap-2">
                        <input data-testid="verify-germany-renovation" type="number" step="1" value={form.renovation_cost}
                          onChange={e => updateField("renovation_cost", e.target.value)}
                          className="w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                          placeholder="0" />
                        <span className="text-white/60 text-[9px]">{sym}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-white/65">Other Purchase Costs</span>
                      <div className="flex items-center gap-2">
                        <input type="number" step="1" value={form.other_purchase_costs}
                          onChange={e => updateField("other_purchase_costs", e.target.value)}
                          className="w-28 bg-[#050505] border border-white/10 text-white px-2 py-1 text-xs font-mono text-right focus:border-[#D4AF37] outline-none transition-all"
                          placeholder="0" />
                        <span className="text-white/60 text-[9px]">{sym}</span>
                      </div>
                    </div>
                    {roi && (
                      <div className="flex justify-between text-xs pt-2 border-t border-[#D4AF37]/20"><span className="text-white font-medium">Total Investment</span><span className="font-mono text-[#D4AF37] font-medium">{sym} {roi.total_investment?.toLocaleString()}</span></div>
                    )}
                  </div>
                )}

                {/* Dubai-specific: Service fee per sqft + Maintenance per sqft */}
                {form.location === "dubai" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Service Fee/sqft p.a. ({sym})</label>
                      <input data-testid="verify-svc-per-sqft" type="number" step="0.01" value={form.service_fee_per_sqft}
                        onChange={e => updateField("service_fee_per_sqft", e.target.value)}
                        className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] outline-none transition-all"
                        placeholder="e.g. 67.54" />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Maintenance/sqft p.a. ({sym})</label>
                      <input data-testid="verify-maint-per-sqft" type="number" step="0.01" value={form.maintenance_per_sqft}
                        onChange={e => updateField("maintenance_per_sqft", e.target.value)}
                        className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] outline-none transition-all"
                        placeholder="e.g. 104" />
                    </div>
                  </div>
                )}

                {/* North Cyprus: Site Fees + Property Tax */}
                {form.location === "north_cyprus" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Site Fees / m&sup2; p.a. ({sym})</label>
                      <input data-testid="verify-cyprus-site-fee" type="number" step="0.01" value={form.maintenance_per_sqft}
                        onChange={e => updateField("maintenance_per_sqft", e.target.value)}
                        className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] outline-none transition-all"
                        placeholder="e.g. 8.14" />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Property Tax p.a. ({sym})</label>
                      <input data-testid="verify-cyprus-property-tax" type="number" step="0.01" value={form.property_tax_annual}
                        onChange={e => updateField("property_tax_annual", e.target.value)}
                        className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] outline-none transition-all"
                        placeholder="Grundsteuer annual" />
                    </div>
                  </div>
                )}

                {/* Germany: Hausgeld + Rücklage */}
                {form.location === "germany" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Hausgeld (n.u.) / mo ({sym})</label>
                      <input data-testid="verify-germany-hausgeld" type="number" step="0.01" value={form.hausgeld_monthly}
                        onChange={e => updateField("hausgeld_monthly", e.target.value)}
                        className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] outline-none transition-all"
                        placeholder="e.g. 120" />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Instandhaltungsr&uuml;cklage / mo ({sym})</label>
                      <input data-testid="verify-germany-ruecklage" type="number" step="0.01" value={form.instandhaltungsruecklage_monthly}
                        onChange={e => updateField("instandhaltungsruecklage_monthly", e.target.value)}
                        className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] outline-none transition-all"
                        placeholder="e.g. 50" />
                    </div>
                  </div>
                )}

                {/* Financing Toggle */}
                <div className="border border-white/[0.10] bg-white/[0.02] p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] tracking-[0.15em] uppercase text-white/60">Bank Financing</p>
                    <button
                      type="button"
                      onClick={() => updateField("is_financed", !form.is_financed)}
                      data-testid="verify-financing-toggle"
                      className={`w-11 h-6 rounded-full transition-colors relative ${form.is_financed ? "bg-[#D4AF37]" : "bg-white/10"}`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.is_financed ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                  </div>
                  {form.is_financed && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="grid grid-cols-3 gap-3 pt-1">
                      <div>
                        <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Loan Amount ({sym})</label>
                        <input data-testid="verify-loan-amount" type="number" step="1" value={form.loan_amount}
                          onChange={e => updateField("loan_amount", e.target.value)}
                          className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] outline-none transition-all"
                          placeholder="e.g. 1425000" />
                      </div>
                      <div>
                        <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Interest Rate %</label>
                        <input data-testid="verify-interest" type="number" step="0.01" value={form.interest_rate_pct}
                          onChange={e => updateField("interest_rate_pct", e.target.value)}
                          className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] outline-none transition-all"
                          placeholder="e.g. 4.5" />
                      </div>
                      <div>
                        <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Amortization %</label>
                        <input data-testid="verify-amortization" type="number" step="0.01" value={form.amortization_pct}
                          onChange={e => updateField("amortization_pct", e.target.value)}
                          className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] outline-none transition-all"
                          placeholder="Optional" />
                      </div>
                    </motion.div>
                  )}
                  {form.is_financed && roi?.is_financed && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-1.5 pt-1 text-xs">
                      <div className="flex justify-between"><span className="text-white/60">Loan Amount</span><span className="font-mono text-white">{sym} {roi.loan_amount?.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-white/60">Downpayment</span><span className="font-mono text-white">{sym} {roi.downpayment?.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-white/60">Equity Invested (Eigenkapital)</span><span className="font-mono text-[#D4AF37]">{sym} {roi.equity_invested?.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-white/60">Annual Interest</span><span className="font-mono text-red-400/70">-{sym} {roi.annual_interest?.toLocaleString()}</span></div>
                      {roi.annual_amortization > 0 && <div className="flex justify-between"><span className="text-white/60">Annual Amortization</span><span className="font-mono text-red-400/70">-{sym} {roi.annual_amortization?.toLocaleString()}</span></div>}
                    </motion.div>
                  )}
                </div>

                {/* Rental Income section — highlighted */}
                <div className="border border-[#D4AF37]/20 bg-[#D4AF37]/[0.03] p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] tracking-[0.15em] uppercase text-[#D4AF37]">Rental Income &amp; ROI</p>
                    {extractedData?.rental_guarantee_found && (
                      <span className="text-[9px] uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5">
                        Rental Guarantee Found
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Monthly Rental Income ({sym}) <span className="text-white/65 normal-case">· optional</span></label>
                      <input data-testid="verify-rental-income" type="number" step="0.01" value={form.monthly_rental_income}
                        onChange={e => updateField("monthly_rental_income", e.target.value)}
                        className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none transition-all"
                        placeholder={extractedData?.rental_guarantee_found ? "Pre-filled from contract" : "Leave blank if not rented"} />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">{(form.location === "dubai" || form.location === "north_cyprus" || form.location === "germany") ? "Other Annual Costs" : "Annual Service Charges"} ({sym})</label>
                      <input data-testid="verify-service-charges" type="number" step="0.01"
                        value={(form.location === "dubai" || form.location === "north_cyprus" || form.location === "germany") ? form.other_annual_costs : form.service_charges_annual}
                        onChange={e => updateField((form.location === "dubai" || form.location === "north_cyprus" || form.location === "germany") ? "other_annual_costs" : "service_charges_annual", e.target.value)}
                        className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm font-mono focus:border-[#D4AF37] outline-none transition-all"
                        placeholder="Optional" />
                    </div>
                  </div>

                  {/* Live ROI KPIs */}
                  {roi && parseFloat(form.monthly_rental_income) > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-3 pt-2"
                      data-testid="roi-preview"
                    >
                      {roi.running_costs_annual > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-white/60">Running Costs (Annual)</span>
                          <span className="font-mono text-red-400/70">-{sym} {roi.running_costs_annual?.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-4 gap-2">
                        <div className="bg-[#050505] border border-white/[0.10] p-3 text-center">
                          <p className="text-[9px] tracking-[0.12em] uppercase text-white/65 mb-1">Monthly Net</p>
                          <p className="font-mono text-sm text-white">{sym} {roi.monthly_cashflow?.toLocaleString()}</p>
                        </div>
                        <div className="bg-[#050505] border border-white/[0.10] p-3 text-center">
                          <p className="text-[9px] tracking-[0.12em] uppercase text-white/65 mb-1">Yearly Net</p>
                          <p className="font-mono text-sm text-white">{sym} {roi.yearly_cashflow?.toLocaleString()}</p>
                        </div>
                        <div className="bg-[#050505] border border-white/[0.10] p-3 text-center">
                          <p className="text-[9px] tracking-[0.12em] uppercase text-white/65 mb-1">Equity Yield</p>
                          <p className="font-mono text-sm text-[#D4AF37]">{roi.equity_yield_pct == null ? "—" : `${roi.equity_yield_pct}%`}</p>
                        </div>
                        <div className="bg-[#050505] border border-white/[0.10] p-3 text-center">
                          <p className="text-[9px] tracking-[0.12em] uppercase text-white/65 mb-1">Gross ROI</p>
                          <p className="font-mono text-sm text-[#D4AF37]">{roi.total_roi_pct}%</p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-1.5 block">Description</label>
                  <textarea data-testid="verify-description" value={form.description}
                    onChange={e => updateField("description", e.target.value)} rows={2}
                    className="w-full bg-[#050505] border border-white/10 text-white px-4 py-2.5 text-sm placeholder:text-white/55 focus:border-[#D4AF37] outline-none resize-none transition-all" />
                </div>

                <button type="submit" disabled={loading} data-testid="confirm-asset-btn"
                  className="w-full bg-[#D4AF37] text-black py-3 text-sm font-medium hover:bg-[#B5952F] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  <Check className="w-4 h-4" />
                  {loading ? "Saving..." : "Confirm & Save to Portfolio"}
                </button>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
